// What the requester changed after a reviewer sent the request back.
//
// Without this, a returned request comes back with no sign of
// what moved — the reviewer has to expand History and read a diff table that,
// for the acceptance form, says nothing but "(form answers changed)". This is
// the one place that answers "what is different from the version I rejected".
//
// It reads the SAME events every detail page already loads for History; no
// request column, no API change. Every flow logs the same shapes:
//   `rejected`  — the reviewer sent it back
//   `modified`  — { changes: { field: { before, after } } }
// and every events feed is newest-first (ORDER BY created_at DESC, id DESC).

const sameValue = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Merge every `modified` event that happened AFTER the most recent `rejected`
 * one into a single { field: { before, after } } map.
 *
 * `before` is taken from the OLDEST such edit (that is the version the
 * reviewer saw when they returned it) and `after` from the NEWEST (what they
 * are looking at now), so three consecutive edits to the same field read as
 * one change rather than three.
 *
 * Returns null when the request was never returned, or when nothing survived
 * (e.g. a field was edited and then edited back).
 *
 * @param events newest-first event feed, as `<flow>/:id/events` returns it
 */
export const changesSinceReturn = (events = []) => {
  const list = events || [];
  const returnedAt = list.findIndex((ev) => ev.action === 'rejected');
  if (returnedAt < 0) return null; // never sent back — nothing to compare against

  // Everything newer than the return, oldest-first so the merge below keeps
  // the earliest `before`.
  const edits = list.slice(0, returnedAt).filter((ev) => ev.action === 'modified').reverse();
  if (edits.length === 0) return null;

  const merged = {};
  edits.forEach((ev) => {
    Object.entries(ev.changes || {}).forEach(([field, diff]) => {
      if (!diff || typeof diff !== 'object') return;
      merged[field] = {
        ...diff,
        before: field in merged ? merged[field].before : diff.before,
        after: diff.after,
      };
    });
  });

  // An edit-and-undo nets out to nothing; do not report it as a change.
  const net = Object.fromEntries(
    Object.entries(merged).filter(([, d]) => !sameValue(d.before, d.after))
  );
  return Object.keys(net).length > 0 ? net : null;
};

// ── Amendment flows (ear / qca / car) need a different read ─────────────────
// Their `modified` event does NOT carry an edit delta. It carries the whole
// amendment payload — { field: { before: the master record's value,
// after: what the request asks it to become } } (wfEarRepository.modify /
// wfQcaRepository.modify / wfCarRepository.modify). Feeding that to
// changesSinceReturn would label the MASTER value as "when you returned it",
// which is simply a different fact.
//
// fsa / qc35a are NOT in this group despite being amendments: they log
// attachmentDiff only, which is a genuine before/after pair.
//
// The edit delta there is the movement of the `after` side: what the request
// was asking for when it was returned, versus what it asks for now. `files` is
// the exception — attachmentDiff writes a real before/after pair — so it keeps
// the ordinary treatment.
const REAL_DELTA_KEYS = new Set(['files']);
const afterSide = (entry) => (entry && typeof entry === 'object' && 'after' in entry ? entry.after : entry);

export const amendmentChangesSinceReturn = (events = []) => {
  const list = events || [];
  const returnedAt = list.findIndex((ev) => ev.action === 'rejected');
  if (returnedAt < 0) return null;

  // Newest payload-carrying event after the return, and the one that was
  // current when the reviewer sent it back (an edit, or the submission).
  const carries = (ev) => (ev.action === 'modified' || ev.action === 'submitted')
    && ev.changes && Object.keys(ev.changes).length > 0;
  const now = list.slice(0, returnedAt).find(carries);
  const atReturn = list.slice(returnedAt).find(carries);
  if (!now || !atReturn) return null;

  const out = {};
  new Set([...Object.keys(now.changes), ...Object.keys(atReturn.changes)]).forEach((field) => {
    if (REAL_DELTA_KEYS.has(field)) return; // handled by the ordinary pass below
    const before = afterSide(atReturn.changes[field]);
    const after = afterSide(now.changes[field]);
    if (!sameValue(before, after)) out[field] = { before, after };
  });

  // Attachments still diff the ordinary way.
  const plain = changesSinceReturn(list) || {};
  REAL_DELTA_KEYS.forEach((k) => { if (plain[k]) out[k] = plain[k]; });

  return Object.keys(out).length > 0 ? out : null;
};
